import Button from '@brands/Components/Button/Button';
import DateTextField from '@brands/Components/Inputs/MuiDateTextField/DateTextField';
import MultiSelectInputMUI from '@brands/Components/Inputs/MuiMultiSelectInput/MultiSelectInputMUI';
import MUISelectInput from '@brands/Components/Inputs/MuiSelectInput/MUISelectInput';
import PhoneInput from '@brands/Components/Inputs/MuiTelInput/MuiTelInput';
import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import Sidenav from '@brands/Dashboard/Sidenav/Sidenav';
import { HashedUser } from '@brands/services/auth/getUserByHash';
import { listAllLanguages } from '@brands/services/identity/listAllLanguages';
import { genderIdentity, genderOptions, pronounsOptions } from '@brands/Utils/selectButtonOptions';
import { OptionLanguage, stateOptions } from '@brands/Utils/selectOptions';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { E164Number } from 'libphonenumber-js';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { displayErrorDetails } from '../../Utils/displayError';
import RoleCompleteProfileForm from './roleForms/RoleCompleteProfileForm';
import { isRoleSpecificFirstLaunchRole } from './roleForms/roleFormConfig';
import styles from './styles.module.scss';
import { UserCompleteProfileForm, validationSchema } from './types';

interface Props {
  signUp: (data: UserCompleteProfileForm) => Promise<void>;
  invitedUser: HashedUser | null;
}

const CompleteProfile: FC<Props> = ({ signUp, invitedUser }) => {
  // Non-patient roles get a first-launch form mirroring their own account-menu
  // my-profile page. Patient (and any unknown/self-registration flow) keeps the
  // original generic form below.
  if (isRoleSpecificFirstLaunchRole(invitedUser?.role?.name)) {
    return <RoleCompleteProfileForm signUp={signUp} invitedUser={invitedUser} />;
  }

  return <PatientCompleteProfile signUp={signUp} invitedUser={invitedUser} />;
};

const PatientCompleteProfile: FC<Props> = ({ signUp, invitedUser }) => {
  const [loading, setLoading] = useState(false);
  const [languageOptions, setLanguageOptions] = useState<OptionLanguage[]>([]);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const fetchLanguageOptions = async (): Promise<void> => {
    const data = await listAllLanguages();
    const transformedData = data.map((language) => ({
      value: language.locale,
      label: language.name,
      id: language.id,
    }));
    setLanguageOptions(transformedData);
  };

  useEffect(() => {
    fetchLanguageOptions();
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch,
    clearErrors,
  } = useForm<UserCompleteProfileForm>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema()),
    defaultValues: {
      // DO-3968: `basic_info` is optional on `HashedUser` — the backend
      // omits it when the invited user was created via a flow that
      // doesn't pre-populate profile data. The previous `invitedUser?.basic_info.X`
      // only null-guarded `invitedUser`; if `invitedUser` was non-null
      // but `basic_info` was missing, the form crashed with
      // "Cannot read properties of null (reading 'first_name')" and the
      // sign-up page white-screened. `?.` all the way through keeps the
      // form mountable; unpopulated fields fall back to '' / [].
      first_name: invitedUser?.basic_info?.first_name || '',
      middle_name: invitedUser?.basic_info?.middle_name || '',
      last_name: invitedUser?.basic_info?.last_name || '',
      preferred_name: invitedUser?.basic_info?.preferred_name || '',
      pronouns: invitedUser?.basic_info?.pronouns || '',
      gender: invitedUser?.basic_info?.gender || '',
      gender_identity: invitedUser?.basic_info?.gender_identity || '',
      language: invitedUser?.basic_info?.languages?.map((lang) => lang.id?.toString()) || [],
      phone_number: invitedUser?.phone_number || '',
      address1: invitedUser?.address?.address1 || '',
      address2: invitedUser?.address?.address2 || '',
      city: invitedUser?.address?.city || '',
      state: invitedUser?.address?.state || '',
      zip_code: invitedUser?.address?.zip_code || '',
    },
  });

  const onInvalid = (err: any): void => {
    const error = Object.values(err)[0] as any;
    if (error) {
      displayErrorDetails(error);
    }
  };
  const onSubmit = async (data: UserCompleteProfileForm): Promise<void> => {
    setLoading(true);
    try {
      await signUp(data);
    } catch (error: unknown) {
      displayErrorDetails(error);
    }
    setLoading(false);
  };
  return (
    <>
      <Sidenav isVisibleOnMobile hideDefaultOptions authLoog />
      <div className={styles.body}>
        <div className={styles.leftSection}>
          <div className={styles.container}>
            <form className={styles.formContainer} onSubmit={handleSubmit(onSubmit, onInvalid)}>
              <span className={styles.formTitle}>
                <span className={`${styles.formTitleWelcome} fs-unmask`} data-testid="complete-profile-title">
                  Complete your profile
                </span>
                <span className={`fs-unmask ${styles.formTitleWelcomeSub}`}>
                  We need a bit more information to finish creating your account
                </span>
              </span>
              <div className={`${styles.inputWrapRow} ${styles.inputWrapEqualRow}`}>
                <div className={styles.inputWrap}>
                  <TextField
                    {...register('first_name')}
                    data-testid="complete-profile-first_name"
                    label="First name"
                    errors={errors.first_name ? errors : {}}
                    name="first_name"
                    onChange={(e: any) => {
                      if (errors.first_name) clearErrors('first_name');
                      setValue('first_name', e.target.value);
                    }}
                    required
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                  />
                </div>
                <div className={styles.inputWrap}>
                  <TextField
                    {...register('middle_name')}
                    data-testid="complete-profile-middle_name"
                    label="Middle name (optional)"
                    errors={errors.middle_name ? errors : {}}
                    name="middle_name"
                    onChange={(e: any) => {
                      if (errors.middle_name) clearErrors('middle_name');
                      setValue('middle_name', e.target.value);
                    }}
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                  />
                </div>
                <div className={styles.inputWrap}>
                  <TextField
                    {...register('last_name')}
                    data-testid="complete-profile-last_name"
                    label="Last name"
                    errors={errors.last_name ? errors : {}}
                    name="last_name"
                    onChange={(e: any) => {
                      if (errors.last_name) clearErrors('last_name');
                      setValue('last_name', e.target.value);
                    }}
                    required
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                  />
                </div>
              </div>
              <div className={`${styles.inputWrapRow} ${styles.inputWrapEqualRow}`}>
                <div className={styles.inputWrap}>
                  <TextField
                    {...register('preferred_name')}
                    data-testid="complete-profile-preferred_name"
                    label="Preferred name (optional)"
                    errors={errors.preferred_name ? errors : {}}
                    name="preferred_name"
                    onChange={(e: any) => {
                      if (errors.preferred_name) clearErrors('preferred_name');
                      setValue('preferred_name', e.target.value);
                    }}
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.createAccountPreferredName}`}
                  />
                </div>
                <div className={styles.inputWrap}>
                  <DateTextField
                    {...register('date_of_birth')}
                    data-testid="complete-profile-date_of_birth"
                    label="Date of birth"
                    errors={errors.date_of_birth ? errors : {}}
                    name="date_of_birth"
                    value={invitedUser?.basic_info?.date_of_birth ? dayjs(invitedUser.basic_info.date_of_birth) : null}
                    onChange={(newValue: any) => {
                      if (errors.date_of_birth) clearErrors('date_of_birth');

                      let formattedDate = '';
                      if (newValue) {
                        formattedDate = dayjs(newValue).format('MM/DD/YYYY');
                      }

                      setValue('date_of_birth', formattedDate, { shouldValidate: true });
                    }}
                    required
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.completeProfileDOB}`}
                  />
                </div>
              </div>
              <div className={styles.inputWrapRow}>
                <div className={styles.inputWrap}>
                  <MUISelectInput
                    {...register('gender')}
                    data-testid="complete-profile-gender"
                    label="Gender assigned at birth"
                    errors={errors.gender ? errors : {}}
                    value={getValues('gender') || ''}
                    name="gender"
                    onChange={(e: any) => {
                      if (errors.gender) clearErrors('gender');
                      setValue('gender', e, { shouldValidate: true });
                    }}
                    required
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.completeProfileClassGender}`}
                    options={genderOptions}
                  />
                </div>
                <div className={styles.inputWrap}>
                  <MUISelectInput
                    {...register('gender_identity')}
                    data-testid="complete-profile-gender-identity"
                    label="Gender identity"
                    value={watch('gender_identity') || ''}
                    errors={errors.gender_identity ? errors : {}}
                    name="gender_identity"
                    onChange={(e: any) => {
                      if (errors.gender_identity) clearErrors('gender_identity');
                      setValue('gender_identity', e);
                    }}
                    required
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.completeProfileClassIdentity}`}
                    options={genderIdentity}
                  />
                </div>
                <div className={styles.inputWrap}>
                  <MUISelectInput
                    {...register('pronouns')}
                    data-testid="complete-profile-pronouns"
                    label="Preferred pronouns (optional)"
                    errors={errors.pronouns ? errors : {}}
                    value={watch('pronouns') || ''}
                    name="pronouns"
                    onChange={(e: any) => {
                      if (errors.pronouns) clearErrors('pronouns');
                      setValue('pronouns', e);
                    }}
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.completeProfileClassPronouns}`}
                    options={pronounsOptions}
                  />
                </div>
              </div>
              <div className={`${styles.inputWrapRow} ${styles.inputWrapEqualRow}`}>
                <div className={styles.inputWrap}>
                  <MultiSelectInputMUI
                    {...register('language')}
                    data-testid="complete-profile-language"
                    label="Languages"
                    errors={errors.language ? errors : {}}
                    name="language"
                    onChange={(selectedValues: any) => {
                      if (errors.language) clearErrors('language');
                      setValue('language', selectedValues, { shouldValidate: true });
                    }}
                    withId
                    value={
                      Array.isArray(watch('language')) && typeof watch('language')[0] === 'string'
                        ? (watch('language') as string[])
                        : Array.isArray(watch('language')) && typeof watch('language')[0] === 'object'
                          ? ((watch('language') as OptionLanguage[]).map((lang: OptionLanguage) =>
                              lang.id?.toString(),
                            ) as string[])
                          : []
                    }
                    required
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.completeProfileLanguage}`}
                    options={languageOptions}
                  />
                </div>
                <div className={styles.inputWrap}>
                  <PhoneInput
                    {...register('phone_number')}
                    data-testid="complete-profile-phone_number"
                    value={watch('phone_number') as E164Number}
                    id="phone_number"
                    name="phone_number"
                    onChange={(val: string | undefined) => {
                      if (val === undefined || val === '') {
                        setValue('phone_number', '', { shouldValidate: true });
                        return;
                      }
                      setValue('phone_number', val, { shouldValidate: true });
                    }}
                    required
                    label="Phone number"
                    errors={errors}
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.completeProfilePhone}`}
                    ref={phoneInputRef}
                  />
                </div>
              </div>
              <div className={`${styles.inputWrapRow} ${styles.inputWrapEqualRow}`}>
                <div className={styles.inputWrap}>
                  <TextField
                    {...register('address1')}
                    data-testid="complete-profile-address1"
                    label="Address"
                    errors={errors.address1 ? errors : {}}
                    name="address1"
                    onChange={(e: any) => {
                      if (errors.address1) clearErrors('address1');
                      setValue('address1', e.target.value);
                    }}
                    required
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.completeProfileAddress}`}
                  />
                </div>
                <div className={styles.inputWrap}>
                  <TextField
                    {...register('address2')}
                    data-testid="complete-profile-address2"
                    label="Apartment, suite, etc. (optional)"
                    errors={errors.address2 ? errors : {}}
                    name="address2"
                    onChange={(e: any) => {
                      if (errors.address2) clearErrors('address2');
                      setValue('address2', e.target.value);
                    }}
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.completeProfileAddress2}`}
                  />
                </div>
              </div>
              <div className={`${styles.inputWrapRow} ${styles.inputWrapEqualRow}`}>
                <div className={styles.inputWrap}>
                  <TextField
                    {...register('city')}
                    data-testid="complete-profile-city"
                    label="City"
                    errors={errors.city ? errors : {}}
                    name="city"
                    onChange={(e: any) => {
                      if (errors.city) clearErrors('city');
                      setValue('city', e.target.value);
                    }}
                    required
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                  />
                </div>
                <div className={styles.inputWrap}>
                  <MUISelectInput
                    {...register('state')}
                    data-testid="complete-profile-state"
                    label="State"
                    errors={errors.state ? errors : {}}
                    name="state"
                    onChange={(e: any) => {
                      if (errors.state) clearErrors('state');
                      setValue('state', e);
                    }}
                    value={watch('state') || ''}
                    required
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                    options={stateOptions}
                  />
                </div>
              </div>
              <div className={`${styles.inputWrapRow} ${styles.inputWrapEqualRow}`}>
                <div className={styles.inputWrap}>
                  <TextField
                    {...register('zip_code')}
                    data-testid="complete-profile-zip-code"
                    label="Zip code"
                    errors={errors.zip_code ? errors : {}}
                    name="zip_code"
                    onChange={(e: any) => {
                      if (errors.zip_code) clearErrors('zip_code');
                      setValue('zip_code', e.target.value, { shouldValidate: true });
                    }}
                    required
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                  />
                </div>
                <div className={styles.inputWrap}>
                  <TextField
                    {...register('d1Code')}
                    data-testid="complete-profile-d1Code"
                    name="d1Code"
                    label="Dentistry.One code (optional)"
                    errors={errors.d1Code ? errors : {}}
                    onChange={(e) => {
                      if (errors.d1Code) clearErrors('d1Code');
                      setValue('d1Code', e.target.value, { shouldValidate: true });
                    }}
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                  />
                </div>
              </div>
              <div className={styles.buttonDiv}>
                <Button
                  type="submit"
                  data-testid="complete-profile-save-profile"
                  disabled={loading}
                  className={`${loading && styles.inActiveBtn} fs-unmask ${styles.formBtn}`}
                  style={{ borderRadius: '32px', height: '42px', width: 'auto' }}
                >
                  Save Profile
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default CompleteProfile;
